
import img0 from './Create_GPO.png';
import img1 from './GPO_Name.png';
import img2 from './Edit_GPO.png';
import img3 from './GPO_Editor.png';
import img4 from './Add_Script.png';
import img5 from './Select_PowerShell_Script1.png';
import img6 from './Select_PowerShell_Script2.png';
import img7 from './Save_GPO.png';
import img8 from './Force_Update.png';
  
const content = `# SecureFile Silent Installation and Configuration

## Objective

***
To efficiently deploy the OnData SecureFile client in an enterprise environment, the silent installation and registration process is supported. As OnData 
SecureFile currently supports the Windows operating systems only, the process below illustrates how to use group policy to silently install and register 
multiple computers in an organization unit within a Windows domain controller.

The support for other major operating systems, such as MacOS and Linux, is on the product roadmap. Support for those operation systems will be announced as 
it is made available.  

***
  
## Silent Installation
  
The OnData SecureFile release package comes with one installation file named \`SecureFileInstaller.exe\` for the Windows platform. To silently install the 
SecureFile client, run the installer with the \`/VERYSILENT\` option from a command prompt or script file: \`SecureFileInstaller.exe /VERYSILENT\`  

If a system reboot is required after the installation, the installer will reboot the system automatically without prompting the user for any input.
  
Please note that the OnData SecureFile has the following dependencies. If any of those dependencies is not installed on your computer already, 
the \`SecureFileInstaller.exe\` will install it automatically.  
a. Visual C++ 14 Runtime Libraries (x64)  
b. .NET Desktop Runtime 8.x (x64)  
c. Edge WebView runtime  
  
## Silent Configuration
  
After the SecureFile client is installed the first time, you may run the registration from a command prompt or script file as well. Assuming the SecureFile 
client is installed to the default path, the following command can be used to silently register the SecureFile client. 
  
\`"C:/Program Files/OnData/OnData SecureFile/SecureFile.exe" -r <registration_code>\`  
  
The \`<registration_code>\` must be an active registration code obtained from the OnData Portal. For details on how to obtain a new registration code from 
the OnData Portal, please refer to the \`Configure SecureFile\` section of the [**Install SecureFile**](reference_doc#how-to_guides/securefile/install_securefile) instructions.  
  
## Use Group Policy for Silent Installation and Configuration
  
Below are the step-by-step instructions on how to deploy the OnData SecureFile client using group policy.  
a. Identify an existing or create a new network share to store the files needed for the deployment. We assume the UNC path is &bsol;&bsol;WINSERVER2019&bsol;software&bsol; in 
this example.  
b. Copy the \`SecureFileInstaller.exe\` file to the network share &bsol;&bsol;WINSERVER2019&bsol;software&bsol;   
c. In order for the \`SecureFileInstaller.exe\` not to download the dependencies on every computer, you can download all dependencies and place them under 
the network share &bsol;&bsol;WINSERVER2019&bsol;software&bsol;. We will silently install all dependencies first, then run the \`SecureFileInstaller.exe\` to install OnData SecureFile. 
The dependencies can be downloaded from the following links:  
   * [Visual C++ 14 Runtime Libraries (x64)](https://aka.ms/vs/17/release/14.38.33130/VC_Redist.x64.exe)  
   * [.NET Desktop Runtime 8.x (x64)](https://go.microsoft.com/fwlink/?linkid=2250487)  
   * [Edge WebView runtime](http://go.microsoft.com/fwlink/p/?LinkId=2124703)  

d. Prepare a PowerShell script file to install the dependencies first, then install OnData SecureFile. After the installation is completed, run the registration 
process. The following lists the primary commands for the script file. You may name the file as \`InstallSecureFile.ps1\` or anything else as preferred.  
  
\`\`\`powershell
"\\\\WINSERVER2019\\software\\SecureFileInstaller.exe" /VERYSILENT
"C:\\Program Files\\OnData\\OnData SecureFile\\SecureFile.exe" -r XXXX-XXX-XXX

\`\`\`  
e. As the \`InstallSecureFile.ps1\` script is designed to run during computer startup, we need to first check whether all components are installed before and 
whether the registration has been completed before. If yes, we should skip those steps. Below is a sample implementation of the \`InstallSecureFile.ps1\` script. 
It takes two parameters: a valid registration code from the OnData Portal, and the version number of the OnData SecureFile to be installed. Please note that you 
may need to adjust this script file if your UNC path is not &bsol;&bsol;WINSERVER2019&bsol;software&bsol;.  
  
\`\`\`powershell
Param (
	[Parameter(Mandatory = $true)]
	[string]
	$RegCode,
	[string]
	$NewVersion = "1.0.0"
)

if (-Not ([Security.Principal.WindowsPrincipal] [Security.Principal.WindowsIdentity]::GetCurrent()).IsInRole([Security.Principal.WindowsBuiltInRole] 'Administrator')) {
	if ([int](Get-CimInstance -Class Win32_OperatingSystem | Select-Object -ExpandProperty BuildNumber) -ge 6000) {
		$Command = "-File \`"" + $MyInvocation.MyCommand.Path + "\`" " + "-RegCode " + $RegCode + " -NewVersion " + $NewVersion
		Start-Process -FilePath PowerShell.exe -Verb RunAs -ArgumentList $Command
		Exit
	}
}

function Test-Registry($regPath){
	try
	{
		$exists = Get-ItemProperty $regPath -ErrorAction SilentlyContinue
		Write-Host "Test-Registry: $exists"
		if (($exists -eq $null) -or ($exists.Length -eq 0))
		{
			return $false
		}
		else
		{
			return $true
		}
	}
	catch
	{
		return $false
	}
}

function Test-RegistryKey($regPath, $keyName){
	try
	{
		$exists = Get-ItemProperty $regPath -Name $keyName -ErrorAction SilentlyContinue
		Write-Host "Test-RegistryKey: $exists"
		if (($exists -eq $null) -or ($exists.Length -eq 0))
		{
			return $false
		}
		else
		{
			return $true
		}
	}
	catch
	{
		return $false
	}
}

function NetRuntimeIsMissing(){
	$minimumVersion = "8.0.0"
	$maximumExclusiveVersion = "8.9.0"
	$depend1 = Test-Path -Path "HKLM:\\SOFTWARE\\WOW6432Node\\dotnet\\Setup\\InstalledVersions\\x64\\sharedfx\\Microsoft.WindowsDesktop.App"
	if($depend1){
		$nodeArr = Get-Item -Path "HKLM:\\SOFTWARE\\WOW6432Node\\dotnet\\Setup\\InstalledVersions\\x64\\sharedfx\\Microsoft.WindowsDesktop.App" | Select-Object -ExpandProperty Property
		for($i=0;$i -le ([System.Array]$nodeArr).length; $i++){
			if(([version](([System.Array]$nodeArr)[$i]) -ge [version]$minimumVersion) -and ([version](([System.Array]$nodeArr)[$i]) -le [version]$maximumExclusiveVersion)){
				return $false
			}
		}
		return $true
	}
	else{
		return $true
	}
}

function Webview2RuntimeIsMissing(){
	$isWebview2RuntimeInstall = Test-RegistryKey "HKLM:\\SOFTWARE\\WOW6432Node\\Microsoft\\EdgeUpdate\\Clients\\{F3017226-FE2A-4295-8BDF-00C3A9A7E4C5}" "pv"
	if($isWebview2RuntimeInstall){
		return $false
	}
	else{
		return $true
	}
}

function VCIsMissing(){
	$isVCInstall = Test-RegistryKey "HKLM:\\SOFTWARE\\WOW6432Node\\Microsoft\\VisualStudio\\14.0\\VC\\Runtimes\\X64" "Version"
	if($isVCInstall){
		return $false
	}
	else{
		return $true
	}
}

$isInstall = Test-Registry "HKLM:\\SOFTWARE\\OnData\\SecureFile"
$isRegister = Test-Registry "HKLM:\\SOFTWARE\\OnData\\SecureFile\\Main"
$UNCPath = "\\\\WINSERVER2019\\software"

Write-Host "Start Checking Dependencies"
if(NetRuntimeIsMissing){
	Write-Host ".Net Runtime is missing. Start installing..."
	& $UNCPath\\windowsdesktop-runtime-8.0.0-win-x64.exe /SILENT | Wait-Process
	Write-Host ".Net Runtime installation completed."
}
if(Webview2RuntimeIsMissing){
	Write-Host "Webview2 Runtime is missing. Start installing..."
	& $UNCPath\\MicrosoftEdgeWebview2Setup.exe /SILENT /INSTALL | Wait-Process
	Write-Host "Webview2 Runtime installation completed."
}
if(VCIsMissing){
	Write-Host "Visual C++ Runtime is missing. Start installing..."
	& $UNCPath\\VC_redist.x64.exe /SILENT | Wait-Process
	Write-Host "Visual C++ Runtime installation completed"
}
Write-Host "Completed Checking Dependencies"
if(-not $isInstall){
	Write-Host "Start installing OnData SecureFile..."
	& $UNCPath\\SecureFileInstaller.exe /VERYSILENT | Wait-Process
	Write-Host "Completed installing OnData SecureFile."
} 
else{
	$hasVersionInfo = Test-Registry "HKLM:\\SOFTWARE\\OnData\\SecureFile"
	if($hasVersionInfo){
		$regVersion = Get-ItemPropertyValue -Path "HKLM:\\SOFTWARE\\OnData\\SecureFile" -Name Version
		if($NewVersion -ne $regVersion){
			& $UNCPath\\SecureFileInstaller.exe /VERYSILENT | Wait-Process
		}
	}
}

if(-not $isRegister){
	Write-Host "Start registering this computer with OnData..."
	(C:\\'Program Files\\OnData\\OnData SecureFile'\\SecureFile.exe -r $RegCode) | Wait-Process
	Write-Host "Computer registration completed."
	Start-Sleep -s 10
	$isRegister = Test-Registry "HKLM:\\SOFTWARE\\OnData\\SecureFile\\Main"
	if($isRegister){
		Write-Host "Restarting computer..."
		Restart-Computer -Force
		Write-Host "Issued command to restart this computer."
	}
}

\`\`\`  
  
f. Identify an organization unit (OU) in your active directory that contains the computers on which you would like to install OnData SecureFile. Right click on the 
selected OU and choose the option \`Create a GPO in this domain, and Link it here...\`.  

![Create Group Policy Object](${ img0 })  
  
g. Give a name to the GPO, such as \`Install OnData SecureFile\`.  
  
![Group Policy Object Name](${ img1 })  
  
h. Right click on the newly created GPO and select \`Edit...\`.  
  
![Edit Group Policy Object](${ img2 })  
  
i. In the \`Group Policy Management Editor\`, navigate to \`Computer Configuration -> Policies -> Windows Settings -> Scripts (Startup/Shutdown)\`, and double 
click on \`Startup\`.  
  
![Group Policy Object Editor](${ img3 })    
  
j. Under \`Startup Properties\`, select the \`PowerShell Scripts\` tab, and click on the \`Add...\` button to add a script.  
  
![Add PowerShell Script](${ img4 })  
  
k. In the dialog, click on the \`Browse...\` button to browse for the script file.  
   
![Select PowerShell Script](${ img5 })  
  
l. Do not browse to any other folder location. Instead, just copy and paste the \`InstallSecureFile.ps1\` file to the default Startup folder in the dialog, then 
select that file and click on the \`Open\` button.  
  
![Select PowerShell Script](${ img6 })  
  
m. Provide the required parameters: \`-RegCode <registration_code> -NewVersion <version_number>\`. The \`<registration_code>\` must be an active registration code 
obtained from the OnData Portal. For details on how to obtain a new registration code from the OnData Portal, please refer to the \`Configure SecureFile\` section 
of the [**Install SecureFile**](reference_doc#how-to_guides/securefile/install_securefile) instructions. The \`<version_number>\` is the version of the OnData SecureFile application to be installed, 
such as \`1.0.205\`. Select the \`OK\` button and the \`OK\` button again to save the GPO.  
  
![Save Group Policy Object](${ img7 })  
  
n. Run the \`gpupdate /force\` command from a command prompt to force a background update of all Group Policy settings. When a computer within this OU restarts, 
the OnData SecureFile application will be automatically installed and registered on that computer.  
  
![Update Group Policy Object](${ img8 })  
  
  `
  
export default content;
